/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import cx from 'classnames';
import { useMediaQuery, useMergedRefs, Box, useLayoutEffect, useControlledState, useLatestRef, importCss, isUnitTest, HydrationProvider, } from '../../utils/index.js';
import { ThemeContext } from './ThemeContext.js';
import { ToastProvider, Toaster } from '../Toast/Toaster.js';
/**
 * This component provides global state and applies theme to the entire tree
 * that it is wrapping around.
 *
 * The `theme` prop defaults to "inherit", which looks upwards for closest ThemeProvider
 * and falls back to "light" theme if one is not found.
 *
 * If you want to theme the entire app, you should use this component at the root. You can also
 * use this component to apply a different theme to only a part of the tree.
 *
 * By default, the topmost `ThemeProvider` in the tree will apply the recommended
 * `background-color`. You can override this behavior using `themeOptions.applyBackground`.
 *
 * @example
 * <ThemeProvider theme='os'>
 *  <App />
 * </ThemeProvider>
 *
 * @example
 * <ThemeProvider as='body'>
 *   <App />
 * </ThemeProvider>
 *
 * @example
 * <ThemeProvider theme='dark' themeOptions={{ applyBackground: false }}>
 *  <App />
 * </ThemeProvider>
 */
export const ThemeProvider = React.forwardRef((props, forwardedRef) => {
    const { theme: themeProp = 'inherit', children, themeOptions = {}, portalContainer: portalContainerProp, includeCss = themeProp === 'inherit', ...rest } = props;
    const [rootElement, setRootElement] = React.useState(null);
    const parent = useParentThemeAndContext(rootElement);
    const theme = themeProp === 'inherit' ? parent.theme || 'light' : themeProp;
    // default apply background only for topmost ThemeProvider
    themeOptions.applyBackground ?? (themeOptions.applyBackground = !parent.theme);
    // default inherit highContrast option from parent if also inheriting base theme
    themeOptions.highContrast ?? (themeOptions.highContrast = themeProp === 'inherit' ? parent.highContrast : undefined);
    /**
     * We will portal our portal container into `portalContainer` prop (if specified),
     * or inherit `portalContainer` from context (if also inheriting theme).
     */
    const portaledPortalContainer = portalContainerProp ||
        (themeProp === 'inherit' ? parent.context?.portalContainer : undefined);
    const [portalContainer, setPortalContainer] = useControlledState(null, portaledPortalContainer);
    const contextValue = React.useMemo(() => ({ theme, themeOptions, portalContainer }), 
    // we do include all dependencies below, but we want to stringify the objects as they could be different on each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, JSON.stringify(themeOptions), portalContainer]);
    return (React.createElement(HydrationProvider, null,
        React.createElement(ThemeContext.Provider, { value: contextValue },
            includeCss && rootElement ? (React.createElement(FallbackStyles, { root: rootElement })) : null,
            React.createElement(Root, { theme: theme, themeOptions: themeOptions, ref: useMergedRefs(forwardedRef, setRootElement), ...rest },
                React.createElement(ToastProvider, null,
                    children,
                    portaledPortalContainer ? (ReactDOM.createPortal(React.createElement(Toaster, null), portaledPortalContainer)) : (React.createElement("div", { ref: setPortalContainer, style: { display: 'contents' } },
                        React.createElement(Toaster, null))))))));
});
// ----------------------------------------------------------------------------
const Root = React.forwardRef((props, forwardedRef) => {
    const { theme, children, themeOptions, className, ...rest } = props;
    const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
    const prefersHighContrast = useMediaQuery('(prefers-contrast: more)');
    const shouldApplyDark = theme === 'dark' || (theme === 'os' && prefersDark);
    const shouldApplyHC = themeOptions?.highContrast ?? prefersHighContrast;
    const shouldApplyBackground = themeOptions?.applyBackground;
    return (React.createElement(Box, { className: cx('iui-root', { 'iui-root-background': shouldApplyBackground }, className), "data-iui-theme": shouldApplyDark ? 'dark' : 'light', "data-iui-contrast": shouldApplyHC ? 'high' : 'default', ref: forwardedRef, ...rest }, children));
});
// ----------------------------------------------------------------------------
/**
 * Returns theme information from either parent ThemeContext or by reading the closest
 * data-iui-theme attribute if context is not found.
 *
 * Also returns the ThemeContext itself (if found).
 */
const useParentThemeAndContext = (rootElement) => {
    const parentContext = React.useContext(ThemeContext);
    const [parentThemeState, setParentTheme] = React.useState(parentContext?.theme);
    const [parentHighContrastState, setParentHighContrastState] = React.useState(parentContext?.themeOptions?.highContrast);
    const parentThemeRef = useLatestRef(parentContext?.theme);
    useLayoutEffect(() => {
        // bail if we already have theme from context
        if (parentThemeRef.current) {
            return;
        }
        // find parent theme from closest data-iui-theme attribute
        const closestRoot = rootElement?.parentElement?.closest('[data-iui-theme]');
        if (!closestRoot) {
            return;
        }
        // helper function that updates state to match data attributes from closest root
        const synchronizeTheme = () => {
            setParentTheme(closestRoot?.getAttribute('data-iui-theme'));
            setParentHighContrastState(closestRoot?.getAttribute('data-iui-contrast') === 'high');
        };
        // set theme for initial mount
        synchronizeTheme();
        // use mutation observers to listen to future updates to data attributes
        const observer = new MutationObserver(() => synchronizeTheme());
        observer.observe(closestRoot, {
            attributes: true,
            attributeFilter: ['data-iui-theme', 'data-iui-contrast'],
        });
        return () => {
            observer.disconnect();
        };
    }, [rootElement, parentThemeRef]);
    return {
        theme: parentContext?.theme ?? parentThemeState,
        highContrast: parentContext?.themeOptions?.highContrast ?? parentHighContrastState,
        context: parentContext,
    };
};
// ----------------------------------------------------------------------------
/**
 * When `@itwin/itwinui-react/styles.css` is not imported, we will attempt to
 * dynamically import it (if possible) and fallback to loading it from a CDN.
 */
const FallbackStyles = ({ root }) => {
    useLayoutEffect(() => {
        // bail if styles are already loaded
        if (getComputedStyle(root).getPropertyValue('--_iui-v3-loaded') === 'yes') {
            return;
        }
        // bail if isUnitTest because unit tests don't care about CSS 🤷
        if (isUnitTest) {
            return;
        }
        (async () => {
            try {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                await import('../../../styles.css');
            }
            catch (error) {
                console.log('Error loading styles.css locally', error);
                const css = await importCss('https://cdn.jsdelivr.net/npm/@itwin/itwinui-react@3/styles.css');
                document.adoptedStyleSheets = [
                    ...document.adoptedStyleSheets,
                    css.default,
                ];
            }
        })();
    }, [root]);
    return React.createElement(React.Fragment, null);
};
